import axios from 'axios';
import { isNil } from 'lodash';

import { regenerateToken } from '@api/regenerate-token';
import { AlertType } from '@enums/alert';
import { ErrorCodes } from '@enums/error';
import { showAlert } from '@lib/alert/alert-manager';
import { uuidRegex } from '@lib/regex';
import { AuthTokensType, ClientToken } from '@pages/select-workspace/types';
import { AppRoutes, NON_CLIENT_ROUTES } from '@routes/constants';

export const LocalStorageKeys = {
  IS_LOGGED_IN: 'IS_LOGGED_IN',
  AUTH_TOKENS: 'AUTH_TOKENS',
  LAST_SELECTED_CLIENT: 'LAST_SELECTED_CLIENT',
};

export const SessionStorageKeys = {
  IS_TOKEN_EXPIRED: 'IS_TOKEN_EXPIRED',
};

export const isLoggedInUser = () => {
  return localStorage.getItem(LocalStorageKeys.IS_LOGGED_IN) === 'true';
};

export const parseAuthTokenFromLocalStorage = () => {
  const authTokensString = localStorage.getItem(LocalStorageKeys.AUTH_TOKENS);

  if (authTokensString) {
    return JSON.parse(authTokensString) as AuthTokensType;
  }

  return null;
};

export const getClientTokenFromLocalStorage = ({
  clientUuid,
}: {
  clientUuid: string;
}) => {
  const authTokens = parseAuthTokenFromLocalStorage();

  return authTokens?.clientTokens?.find(
    clientToken => clientToken.clientUuid === clientUuid,
  );
};

export const getClientUuidFromRoute = () => {
  const pathname = window.location.pathname;
  const pathSegments = pathname.split('/').filter(segment => segment !== '');
  // The first segment of path is expected as clientUuid
  const firstSegment = pathSegments[0]?.trim() ?? null;

  // Check if the first segment is not in NON_CLIENT_ROUTES
  // Check if the first segment is an alphanumeric string of length 8
  if (
    firstSegment &&
    !NON_CLIENT_ROUTES.includes(firstSegment) &&
    uuidRegex.test(firstSegment)
  ) {
    return firstSegment;
  }

  return null;
};

export const getAuthToken = ({
  clientUuid,
  chooseLastClientUuid,
  ignoreSingleClientToken, // Last client token should be ignored if there is only single client
}: {
  clientUuid?: string;
  chooseLastClientUuid?: boolean;
  ignoreSingleClientToken?: boolean;
} = {}) => {
  const authTokens = parseAuthTokenFromLocalStorage();

  if (!isNil(authTokens)) {
    const { userToken, clientTokens } = authTokens || {};
    // Before select client, call apis with user token
    if (userToken) {
      return userToken;
    }

    const isSingleClientActive = clientTokens?.length === 1; // To check if there is only single client on a single tab
    const lastClientUuid = clientTokens?.slice(-1)[0]?.clientUuid;
    let currentClientUuid = null;

    if (chooseLastClientUuid) {
      if (isSingleClientActive && ignoreSingleClientToken) {
        return null;
      }
      currentClientUuid = lastClientUuid;
    } else {
      currentClientUuid =
        clientUuid ?? // If clientUuid is passed, use it
        getClientUuidFromRoute() ?? // Else, fetch clientUuid from route
        localStorage.getItem(LocalStorageKeys.LAST_SELECTED_CLIENT) ?? // Else, fetch Uuid from last selected client from local storage
        lastClientUuid; // Else, assume last client as current client and find uuid
    }

    if (!isNil(currentClientUuid)) {
      const clientToken: ClientToken | undefined =
        getClientTokenFromLocalStorage({
          clientUuid: currentClientUuid,
        });

      return clientToken?.token;
    }
  }

  return null;
};

export const addClientTokenToLocalStorage = ({
  clientUuid,
  clientToken,
}: {
  clientUuid: string;
  clientToken: string;
}) => {
  if (!clientUuid || !clientToken) {
    console.error('Cannot add empty uuid or token to localStorage');

    return;
  }

  const authTokensString = localStorage.getItem(LocalStorageKeys.AUTH_TOKENS);
  const clientTokenObject = {
    clientUuid: clientUuid,
    token: clientToken,
  };

  let updatedClientTokens;
  if (authTokensString) {
    const authTokens: AuthTokensType = JSON.parse(authTokensString);

    const clientTokens = authTokens?.clientTokens;
    if (clientTokens) {
      updatedClientTokens = [...clientTokens];

      const index = clientTokens.findIndex(
        (clientTokenData: ClientToken) =>
          clientTokenData.clientUuid === clientUuid,
      );

      if (index >= 0) {
        updatedClientTokens.splice(index, 1);
      }

      updatedClientTokens.push(clientTokenObject);
    } else {
      updatedClientTokens = [clientTokenObject];
    }

    localStorage.setItem(
      LocalStorageKeys.AUTH_TOKENS,
      JSON.stringify({ clientTokens: updatedClientTokens }),
    );

    // set current client uuid ( for last_login api)
    localStorage.setItem(LocalStorageKeys.LAST_SELECTED_CLIENT, clientUuid);
  }
};

export const clearFromLocalStorage = ({
  isLogout = false,
}: { isLogout?: boolean } = {}) => {
  window.location.href = window.location.origin + AppRoutes.LOGIN;
  localStorage.removeItem(LocalStorageKeys.IS_LOGGED_IN);
  localStorage.removeItem(LocalStorageKeys.AUTH_TOKENS);
  localStorage.removeItem(LocalStorageKeys.LAST_SELECTED_CLIENT);

  if (!isLogout) {
    sessionStorage.setItem(SessionStorageKeys.IS_TOKEN_EXPIRED, 'true'); // For showing the token expiry toast
  }
};

export const generateAndSaveTokenAndRedirectOnAccessFailure = async ({
  clientUuidFromUrl,
}: {
  clientUuidFromUrl?: string;
}) => {
  const tokenInLocalStorage = getClientTokenFromLocalStorage({
    clientUuid: clientUuidFromUrl as string,
  });
  if (isNil(tokenInLocalStorage)) {
    try {
      const newToken = await regenerateToken({
        clientUuid: clientUuidFromUrl,
      });

      if (newToken?.accessToken) {
        // Add token to local storage
        addClientTokenToLocalStorage({
          clientUuid: clientUuidFromUrl as string,
          clientToken: newToken.accessToken,
        });

        return { success: true }; // Return true if token is added to local storage
      }
    } catch (error) {
      console.error('error', error);

      // Handle 403 and 404 errors with specific alerts
      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;
        const { code, message, header } = data;

        if (
          status === 403 &&
          code === ErrorCodes.BD_USER_WORKSPACE_ACCESS_DENIED
        ) {
          showAlert({
            type: AlertType.ERROR,
            header: header || 'Access Denied',
            message:
              message || 'You do not have permission to access this workspace.',
          });

          return { success: false, redirectUrl: AppRoutes.SELECT_WORKSPACE };
        }

        if (status === 404 && code === ErrorCodes.BD_CLIENT_NOT_FOUND) {
          showAlert({
            type: AlertType.ERROR,
            header: header || 'Workspace Not Found',
            message: message || 'The requested workspace could not be found.',
          });

          return { success: false, redirectUrl: AppRoutes.SELECT_WORKSPACE };
        }
      }

      return { success: false, redirectUrl: AppRoutes.LOGIN };
    }
  }

  return { success: true }; // Return true if no errors occurred
};
